import { Box, CircularProgress, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Iconify } from 'src/components/iconify';
import { XButton } from '../../components/XButton';
import { appVersionDataProvider } from './dataProvider';
import { AppVersionFormValues, AppVersionInfo } from './types';
import { isForceUpdate } from './utils';
import DetailForm from './DetailForm';
import PreviewDialog from './PreviewDialog';
import { Resources } from '../resource';

export default function AppVersionEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeRecords, setActiveRecords] = useState<AppVersionInfo[]>([]);

  useEffect(() => {
    appVersionDataProvider
      .getList('', {
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'updatedAt', order: 'DESC' },
        filter: {},
      })
      .then(({ data }) => {
        const actives = data.filter((r: AppVersionInfo) => r.active);
        setActiveRecords(actives);
      })
      .catch(() => {});
  }, []);

  const methods = useForm<AppVersionFormValues>();
  const { handleSubmit, reset, watch } = methods;

  const versionCode = watch('versionCode');
  const locales = watch('locales');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    appVersionDataProvider
      .getOne('', { id })
      .then(({ data }) => {
        const versionCode = data.versionCode ?? '';
        const minVersion = data.minVersion ?? '';
        reset({
          versionCode,
          platform: data.platform ?? 'ALL',
          minVersion: isForceUpdate(versionCode, minVersion) ? versionCode : minVersion,
          startAt: data.startAt,
          endAt: data.endAt,
          showFrequency: data.showFrequency ?? 'ONCE_PER_DAY',
          forceUpdate: isForceUpdate(versionCode, minVersion),
          active: data.active ?? false,
          locales: data.locales?.length
            ? data.locales
            : [
                { locale: 'ZH_HANS', title: '', releaseNotes: [''], button: '' },
                { locale: 'EN', title: '', releaseNotes: [''], button: '' },
              ],
        });
      })
      .catch((e: any) =>
        notify(e?.message || translate('module.appVersionManagement.notify.loadOneError'), {
          type: 'error',
        })
      )
      .finally(() => setLoading(false));
  }, [id, reset, notify, translate]);

  const onSubmit = async (values: AppVersionFormValues) => {
    if (!id) return;
    setSaving(true);
    try {
      await appVersionDataProvider.update('', { id, data: values });
      notify(translate('module.appVersionManagement.notify.updateSuccess'), { type: 'success' });
    } catch (e: any) {
      notify(e?.message || translate('module.appVersionManagement.notify.updateError'), {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </DashboardContent>
    );
  }

  return (
    <PermissionGuard resource={'app-version-updates'} action="update" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.appVersionManagement.editTitle')}
          links={[
            {
              name: translate('module.appVersionManagement.breadcrumb.list'),
              href: `/${Resources.appVersionManagement}`,
            },
            { name: translate('module.appVersionManagement.breadcrumb.edit') },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              <XButton
                variant="outlined"
                color="inherit"
                label={translate('module.appVersionManagement.previewDialog')}
                onClick={() => setPreviewOpen(true)}
                sx={{ minWidth: 150 }}
              >
                <Iconify icon="eva:eye-outline" sx={{ mr: 0.5 }} />
              </XButton>
              <XButton
                variant="contained"
                label={
                  saving
                    ? translate('module.appVersionManagement.saving')
                    : translate('module.appVersionManagement.save')
                }
                disabled={saving}
                onClick={handleSubmit(onSubmit)}
              />
            </Stack>
          }
        />

        <FormProvider {...methods}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <DetailForm
              activeByPlatform={Object.fromEntries(
                activeRecords
                  .filter((r) => String(r.id) !== String(id))
                  .map((r) => [r.platform, r.versionCode])
              )}
            />
          </Box>
        </FormProvider>

        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          versionCode={versionCode}
          locales={locales}
          forceUpdate={watch('forceUpdate')}
          displayType="side"
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
